import { builder, useIsPreviewing } from "@builder.io/react";
import { BuilderComponent } from "@builder.io/react";
import { CssBaseline, ThemeProvider } from "@mui/material";
import React from "react";
import {
  dtcTheme,
  EnvironmentWarning,
  registerBuilderComponents,
  useAmplitudeStart,
  useGTMUserId,
  useHc,
  usePageView,
} from "@ph/builder";
import type { GetStaticPaths, GetStaticProps } from "next";
import DefaultErrorPage from "next/error";
import Head from "next/head";
import { prepareBuilderContent } from "../lib/builderSsrUtils";

// Initialize the Builder SDK with your organization's API Key
// Find the API Key on: https://builder.io/account/settings
if (!process.env.NEXT_PUBLIC_BUILDER_API_KEY) {
  throw new TypeError(
    "Missing environment variable NEXT_PUBLIC_BUILDER_API_KEY"
  );
}
builder.init(process.env.NEXT_PUBLIC_BUILDER_API_KEY);

export const getStaticProps: GetStaticProps = async ({ params }) => {
  if (!process.env.NEXT_PUBLIC_BUILDER_CACHE_TTL) {
    throw new TypeError(
      "Missing environment variable NEXT_PUBLIC_BUILDER_API_KEY"
    );
  }
  const cacheTTL = parseInt(process.env.NEXT_PUBLIC_BUILDER_CACHE_TTL);

  // Fetch the first page from Builder that matches the current URL.
  // Use the `userAttributes` field for targeting content.
  // For more, see https://www.builder.io/c/docs/targeting-with-builder
  const page = await builder
    .get("page", {
      userAttributes: {
        urlPath: `/${(params?.page as string[] | undefined)?.join("/") || ""}`,
      },
    })
    .toPromise();

  // Builder.io v8 injects a tracking pixel at render time using Math.random() for its
  // block ID, causing a guaranteed server/client mismatch on every render. The SDK
  // skips injection when a builder-pixel block already exists in content.blocks.
  // We recursively traverse ALL nested content (top-level blocks and inlined symbol
  // content) and pre-inject stable deterministic pixels so every BuilderContent
  // instance finds an existing pixel and skips its random injection.
  //
  // We also pre-merge symbol data props into each symbol's initial state so SSR
  // renders with the same state the client would use after hydration. Without this,
  // `repeat` blocks driven by symbol data (e.g. `state.reviews`) render as empty
  // on the server, causing structural mismatches (e.g. <a> in <div> warnings).
  if (page) {
    prepareBuilderContent(
      page,
      process.env.NEXT_PUBLIC_BUILDER_API_KEY as string
    );
  }

  return {
    props: {
      page: page || null,
    },
    revalidate: cacheTTL,
  };
};

export const getStaticPaths: GetStaticPaths = async () => {
  //  Fetch all published pages for the current model.
  //  Using the `fields` option will limit the size of the response
  //  and only return the `data.url` field from the matching pages.
  const pages = await builder.getAll("page", {
    fields: "data.url", // only request the `data.url` field
    options: { noTargeting: true },
    limit: 0,
  });

  return {
    paths: pages.map((page) => `${page.data?.url}`),
    fallback: "blocking",
  };
};

type BuilderPageContent = React.ComponentProps<
  typeof BuilderComponent
>["content"];

/** Handles the render loop logic
 *  Renders the Builder.io page with title and metadata along with any content from Builder.io itself
 *  Renders an error page if the user is in Live mode (not previewing through the editor) and there is no route match
 */
export default function Page({ page }: { page: BuilderPageContent }) {
  //  This flag indicates if you are viewing the page in the Builder editor.
  const isPreviewing = useIsPreviewing();
  useHc();
  usePageView();
  useGTMUserId();
  useAmplitudeStart();

  //  Add your error page here to return if there are no matching
  //  content entries published in Builder.
  if (!page && !isPreviewing) {
    return <DefaultErrorPage statusCode={404} />;
  }

  const robotString = [
    page?.data?.noIndex && "noindex",
    page?.data?.noFollow && "nofollow",
  ]
    .filter((item) => item)
    .join(", ");

  return (
    <>
      <Head>
        {/* Add any relevant SEO metadata or open graph tags here */}
        <title>{page?.data?.title}</title>
        <meta name="description" content={page?.data?.description} />
        {robotString && (
          <meta key="robots" name="robots" content={robotString} />
        )}
      </Head>
      <ThemeProvider theme={dtcTheme}>
        {/* Render the Builder page */}
        <CssBaseline />
        {!isPreviewing && <EnvironmentWarning />}
        {/* suppressHydrationWarning is needed here because @builder.io/react v8
            generates tracking pixel element IDs using Math.random(), which produces
            different values on every render (server vs client). This is an upstream
            SDK bug in builder.io v8 that cannot be fixed without modifying the SDK. */}
        <div suppressHydrationWarning>
          <BuilderComponent model="page" content={page} />
        </div>
      </ThemeProvider>
    </>
  );
}

registerBuilderComponents();
